import { IAttributes, IAugmentedJQuery, IDirective, IDirectiveFactory, INgModelController, IScope, equals } from "angular";
import Litepicker from "litepicker/dist/nocss/litepicker.umd.js";
import "litepicker/dist/plugins/keyboardnav.js";
import dayjs from "@gtmhub/dayjs";
import { START_OF_DAY_UTC_SUFFIX, datePart } from "./utils";

interface IDaterangepickerScope extends IScope {
  disabled: boolean;
  date: string;
  viewDate: string;
  show(): void;

  // Whether to convert dates to local format, e.g. 021-03-11T00:00:00.000Z
  // By default, dates are converted to ISO string in UTC, e.g. 021-03-11T21:00:00.000Z
  local: boolean;
  minDate: string;
  maxDate: string;
}

const viewFormat = "DD MMMM YYYY";

export class DatepickerDirective implements IDirective {
  restrict = "E";
  template = require("./datepicker.html");
  require = "ngModel";
  scope = {
    disabled: "<ngDisabled",
    date: "=ngModel",
    local: "<",
    minDate: "<",
    maxDate: "<",
  };

  link(scope: IDaterangepickerScope, element: IAugmentedJQuery, attrs: IAttributes, ctrl: INgModelController): void {
    const picker = new Litepicker({
      element: element[0].querySelector(".gh-select-toggle"),
      singleMode: true,
      minDate: scope.minDate ? scope.minDate : null,
      maxDate: scope.maxDate ? scope.maxDate : null,
      firstDay: dayjs.localeData().firstDayOfWeek(),
      lang: dayjs.locale(),
      format: {
        parse: (date: Date | string) => dayjs.utc(date).toDate(),
        output: (date: Date) => dayjs.utc(date).toISOString(),
      },
      plugins: ["keyboardnav"],
    });
    scope.$on("$destroy", () => picker.destroy());

    scope.$watch("date", () => {
      if (scope.date) {
        const date = scope.local ? dayjs(datePart(scope.date)).toISOString() : dayjs(scope.date).startOf("day").toISOString();
        picker.setDate(date);
        scope.viewDate = dayjs(date).format(viewFormat);
      } else {
        delete scope.viewDate;
        picker.clearSelection();
      }
    });

    scope.show = () => {
      if (scope.disabled) {
        return;
      }

      picker.show();
    };

    const onSelected = () => {
      const rawDate = dayjs(picker.getDate().toJSDate());
      const date = scope.local ? rawDate.format("YYYY-MM-DD") + START_OF_DAY_UTC_SUFFIX : rawDate.startOf("day").toISOString();

      if (!equals(date, scope.date)) {
        picker.off("selected", onSelected);
        ctrl.$setViewValue(date);
      }
    };

    picker.on("show", () => {
      picker.on("selected", onSelected);
    });

    picker.on("hide", () => {
      picker.off("selected", onSelected);
    });

    picker.on("render", (ui: Element) => {
      ui.classList.add("gh-datepicker");
    });
  }

  static factory(): IDirectiveFactory {
    return () => new DatepickerDirective();
  }
}
